
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

// Mock patient data for the selector
const mockPatients = [
  { id: 1, name: 'Emily Johnson' },
  { id: 2, name: 'Michael Chen' },
  { id: 3, name: 'Sarah Williams' },
  { id: 4, name: 'David Rodriguez' },
  { id: 5, name: 'Jennifer Lee' },
];

// Mock services
const services = [
  "Dental Implant",
  "Teeth Whitening",
  "Crown Replacement",
  "Root Canal",
  "Dental Cleaning"
];

interface RequestReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (patientId: number, service: string) => void;
}

const RequestReviewModal: React.FC<RequestReviewModalProps> = ({ 
  isOpen, 
  onClose,
  onSubmit
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<number | null>(null);
  const [selectedService, setSelectedService] = useState('');
  const { toast } = useToast();
  
  // We're using the mock data directly instead of useCaseFile hook to avoid hook rules issues
  const filteredPatients = searchTerm
    ? mockPatients.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : mockPatients;

  const handleSubmit = () => {
    if (!selectedPatient) {
      toast({
        description: "Please select a patient",
        variant: "destructive"
      });
      return;
    }
    
    if (!selectedService) {
      toast({
        description: "Please select a service",
        variant: "destructive"
      });
      return;
    }
    
    onSubmit(selectedPatient, selectedService);
    toast({
      description: "Review request sent successfully",
    });
    
    // Reset form
    setSelectedPatient(null);
    setSelectedService('');
    setSearchTerm('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Request Review from Patient</DialogTitle>
          <DialogDescription>
            Send a review invitation to a patient for a service you provided.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Search Patient
            </label>
            <Input
              placeholder="Search by patient name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Select Patient
            </label>
            <Select 
              value={selectedPatient?.toString() || ''} 
              onValueChange={(value) => setSelectedPatient(parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a patient" />
              </SelectTrigger>
              <SelectContent>
                {filteredPatients.map(p => (
                  <SelectItem key={p.id} value={p.id.toString()}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Select Service
            </label>
            <Select 
              value={selectedService} 
              onValueChange={setSelectedService}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent>
                {services.map(service => (
                  <SelectItem key={service} value={service}>
                    {service}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>
            Send Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RequestReviewModal;
